import { ApplicationRef, Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { concat, first, interval } from 'rxjs';
import { filter, take } from 'rxjs/operators';

/** De quanto em quanto tempo perguntar ao servidor se saiu versão nova. */
const UPDATE_CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000;

/**
 * Mantém o PWA instalado na versão publicada mais recente.
 *
 * O service worker do Angular baixa a versão nova em segundo plano, mas a aba continua rodando a
 * antiga até recarregar. Sem alguém provocar esse recarregamento, quem instalou o app poderia
 * ficar semanas numa versão velha — o usuário de app instalado não fecha a aba como faz no
 * navegador.
 *
 * O recarregamento acontece **na próxima troca de tela**, e não no instante em que a versão fica
 * pronta: recarregar no meio de um formulário de ajuste perderia o que o usuário digitou. Na troca
 * de tela ele já está saindo dali, então a interrupção é invisível.
 */
@Injectable({ providedIn: 'root' })
export class PwaUpdateService {
  private readonly updates = inject(SwUpdate);
  private readonly appRef = inject(ApplicationRef);
  private readonly router = inject(Router);

  private reloadOnNextNavigation = false;

  init(): void {
    if (!this.updates.isEnabled) return;

    this.scheduleVersionChecks();
    this.reloadWhenNewVersionIsReady();
    this.recoverFromBrokenCache();
  }

  /**
   * Só começa a perguntar depois que a aplicação estabiliza: no arranque ela está ocupada
   * carregando traduções, permissões e a primeira tela, e uma verificação aí competiria com isso.
   */
  private scheduleVersionChecks(): void {
    const stable$ = this.appRef.isStable.pipe(first((isStable) => isStable));
    concat(stable$, interval(UPDATE_CHECK_INTERVAL_MS)).subscribe(() => {
      this.updates.checkForUpdate().catch(() => {
        // Sem rede ou servidor fora: a próxima verificação tenta de novo.
      });
    });
  }

  private reloadWhenNewVersionIsReady(): void {
    this.updates.versionUpdates
      .pipe(filter((event) => event.type === 'VERSION_READY'))
      .subscribe(() => {
        this.reloadOnNextNavigation = true;
        this.router.events
          .pipe(filter((event) => event instanceof NavigationEnd), take(1))
          .subscribe(() => {
            if (!this.reloadOnNextNavigation) return;
            this.reloadOnNextNavigation = false;
            this.updates.activateUpdate().then(() => this.reload());
          });
      });
  }

  /**
   * O cache pode ficar irrecuperável quando o servidor apaga arquivos de uma versão que a aba
   * ainda referencia — típico de dois deploys seguidos com a aba aberta. Nesse estado o app
   * quebraria ao carregar qualquer trecho novo; recarregar busca tudo do servidor outra vez.
   */
  private recoverFromBrokenCache(): void {
    this.updates.unrecoverable.subscribe(() => this.reload());
  }

  /** Isolado num método para o spec conseguir substituir — `location.reload` derruba o Karma. */
  private reload(): void {
    document.location.reload();
  }
}
